import type { ReactNode } from 'react';

/**
 * The navigation map, shared by the Layout bars and the More screen.
 *
 * DECISION — five fixed tabs + a More screen, not one bar listing every module.
 * Ten items in a phone-width bar forced 10px labels and 36px targets; five keeps
 * labels at 12px and every target comfortably past 44px. Everything that isn't a
 * daily tab lives on /more as full-width rows. Calendar holds the fifth slot and
 * Chores leads the More list — chosen deliberately (design review, Aug 31 2026),
 * not by build order. Board and Where are absent from BOTH lists: Board is
 * retired (/board redirects home), Where keeps its Home tile. Insurance has no
 * row here because its route lives under /money — a row would light the Money
 * tab, not More; reach it inside the Money module instead.
 */
export interface NavItem {
  to: string;
  label: string;
  adultOnly?: boolean;
  childOnly?: boolean;
  /** One-line explainer shown under the label on the More screen. Only for
      labels that don't explain themselves (a kid facing "Ask" or "House"). */
  hint?: string;
  icon: ReactNode;
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const PRIMARY_NAV: NavItem[] = [
  {
    to: '/',
    label: 'Home',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M3 10.5 12 3l9 7.5" />
        <path d="M5 9.5V20h14V9.5" />
      </svg>
    ),
  },
  {
    to: '/money',
    label: 'Money',
    adultOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <rect x="2.5" y="6" width="19" height="13" rx="2.5" />
        <path d="M2.5 10.5h19" />
      </svg>
    ),
  },
  {
    to: '/allowance',
    // "Allowance", not "Money": same slot as the adults' tab but a different
    // destination, and on shared screens the differing name keeps that honest.
    label: 'Allowance',
    childOnly: true, // kids' primary money view; adults reach each kid from their hub
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v8M9.5 10a2 2 0 0 1 2-1.5h1a1.75 1.75 0 0 1 0 3.5h-1a1.75 1.75 0 0 0 0 3.5h1a2 2 0 0 0 2-1.5" />
      </svg>
    ),
  },
  {
    to: '/meals',
    label: 'Meals',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M4 11h16a7 7 0 0 1-7 7h-2a7 7 0 0 1-7-7Z" />
        <path d="M7 8c0-1.5 1-2 1-3.5M12 8c0-1.5 1-2 1-3.5M17 8c0-1.5 1-2 1-3.5" />
      </svg>
    ),
  },
  {
    to: '/calendar',
    label: 'Calendar',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
        <path d="M3.5 10h17M8 3.5v3M16 3.5v3" />
      </svg>
    ),
  },
];

export const MORE_NAV: NavItem[] = [
  {
    to: '/chores',
    label: 'Chores',
    // Broom, not a checkmark — the check collided with the Reminders glyph for
    // pre-readers. Same paths as the home tile's broom (modules/home/ui.tsx).
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M14 4 9.5 8.5" />
        <path d="m7 11 6-6 3 3-6 6z" />
        <path d="M9 13 4.5 20h11.2c1 0 1.6-1 1.2-1.9L15 14" />
      </svg>
    ),
  },
  {
    to: '/wardrobe',
    label: 'Wardrobe',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M12 6a2 2 0 1 1 2-2" />
        <path d="M12 6 3.5 12.5a1.5 1.5 0 0 0 .9 2.7h15.2a1.5 1.5 0 0 0 .9-2.7L12 6Z" />
      </svg>
    ),
  },
  {
    to: '/birthdays',
    label: 'Birthdays',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M5 12h14a1.5 1.5 0 0 1 1.5 1.5V20h-17v-6.5A1.5 1.5 0 0 1 5 12Z" />
        <path d="M8 12V9.5M12 12V9.5M16 12V9.5" />
        <path d="M8 7c0-1 .5-1.3.5-2M12 7c0-1 .5-1.3.5-2M16 7c0-1 .5-1.3.5-2" />
        <path d="M3.5 16.5c1.5 1.2 3 1.2 4.5 0s3-1.2 4.5 0 3 1.2 4.5 0 3-1.2 4 0" />
      </svg>
    ),
  },
  {
    to: '/reminders',
    label: 'Reminders',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M4 6h10M4 12h10M4 18h6" />
        <path d="m14 17 2.5 2.5L21 15" />
      </svg>
    ),
  },
  {
    to: '/ask',
    label: 'Ask',
    hint: 'Ask the house assistant anything',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-5 4V5.5Z" />
        <path d="M10 8.5a2 2 0 0 1 2-1.5 1.9 1.9 0 0 1 2 1.9c0 1.3-2 1.6-2 2.9" />
        <path d="M12 14.2v.05" />
      </svg>
    ),
  },
  {
    // Adult-only here is a courtesy so kids don't see a dead link; /api/wellness is
    // gated in the Worker and member-scoped inside its router, which is the real boundary.
    to: '/wellness',
    label: 'Wellness',
    adultOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M3 12.5h4l2-4 3 8 2.5-5 1.5 2.5h5" />
      </svg>
    ),
  },
  {
    to: '/vault',
    label: 'House',
    adultOnly: true,
    hint: 'Codes, documents and house info',
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <circle cx="8" cy="12" r="3.5" />
        <path d="M11.5 12H21M18 12v3M14.5 12v2" />
      </svg>
    ),
  },
  {
    to: '/admin',
    label: 'Settings',
    adultOnly: true,
    // Sliders, not a gear — the radial-ray gear read as a sun/brightness glyph.
    icon: (
      <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
        <path d="M21 5h-5M12 5H3M21 12h-9M8 12H3M21 19h-5M12 19H3" />
        <path d="M14 3v4M8 10v4M16 17v4" />
      </svg>
    ),
  },
];

export const MORE_ICON = (
  <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <circle cx="5" cy="12" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
  </svg>
);

export function visibleItems(items: NavItem[], role: string | null) {
  return items.filter(
    (i) => (!i.adultOnly || role === 'adult') && (!i.childOnly || role === 'child')
  );
}

/**
 * True when the current path belongs to a More-screen destination, so the More
 * tab stays lit while you're inside one. Paths under a primary tab (e.g.
 * /money/insurance) are claimed by that tab's own NavLink matching instead.
 */
export function isMorePath(pathname: string): boolean {
  if (pathname === '/more') return true;
  const primaryPrefixes = PRIMARY_NAV.map((i) => i.to).filter((p) => p !== '/');
  return MORE_NAV.some(
    (i) =>
      (pathname === i.to || pathname.startsWith(`${i.to}/`)) &&
      !primaryPrefixes.some((p) => i.to === p || i.to.startsWith(`${p}/`))
  );
}
